import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import { DateInput } from '@/components/ui/DateInput'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { TimePicker, type TimeValue } from '@/components/ui/time-picker'
import { type SalesLead, useClearFollowUpReminder, useSetFollowUpReminder } from '@/models/salesLead'

const DEFAULT_TIME: TimeValue = { hours: 9, minutes: 0 }

function combineDateTime(date: Date, time: TimeValue): Date {
  const d = new Date(date)
  d.setHours(time.hours, time.minutes, 0, 0)
  return d
}

export function FollowUpReminderDialog({
  lead,
  open,
  onOpenChange,
}: {
  lead: SalesLead
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const setMutation = useSetFollowUpReminder()
  const clearMutation = useClearFollowUpReminder()

  const existing = lead.followUpReminder ? new Date(lead.followUpReminder.reminderDate) : undefined
  const [date, setDate] = useState<Date | undefined>(existing)
  const [time, setTime] = useState<TimeValue>(
    existing ? { hours: existing.getHours(), minutes: existing.getMinutes() } : DEFAULT_TIME
  )
  const [note, setNote] = useState(lead.followUpReminder?.message ?? '')

  // Re-sync when the dialog re-opens for a (possibly different) lead.
  useEffect(() => {
    if (!open) return
    const next = lead.followUpReminder ? new Date(lead.followUpReminder.reminderDate) : undefined
    setDate(next)
    setTime(next ? { hours: next.getHours(), minutes: next.getMinutes() } : DEFAULT_TIME)
    setNote(lead.followUpReminder?.message ?? '')
  }, [open, lead.followUpReminder])

  const handleSave = () => {
    if (!date) return
    const when = combineDateTime(date, time)
    setMutation.mutate(
      { id: lead.id, reminderDate: when.toISOString(), note },
      { onSuccess: () => onOpenChange(false) }
    )
  }

  const handleRemove = () => {
    clearMutation.mutate(lead.id, { onSuccess: () => onOpenChange(false) })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Follow-up reminder</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <Label>Date</Label>
            <DateInput value={date} onChange={setDate} />
          </div>
          <div className="flex flex-col gap-1">
            <Label>Time</Label>
            <TimePicker value={time} onChange={setTime} />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="follow-up-note">Note</Label>
            <Textarea
              id="follow-up-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional note (defaults to “Follow up {company}”)"
            />
          </div>
        </div>
        <DialogFooter className="justify-between sm:justify-between">
          {lead.followUpReminder ? (
            <Button variant="outline" onClick={handleRemove} disabled={clearMutation.isPending}>
              Remove follow-up
            </Button>
          ) : (
            <span />
          )}
          <Button onClick={handleSave} disabled={!date || setMutation.isPending}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
